import type { SourceView } from "./types";

/**
 * What a source is, derived from its status and its latest sync run (`SourceView.sync`). A
 * source is `ready` only once its whole run finished; `warnings` is ready with pages that failed.
 */
export type SyncSource = Pick<SourceView, "status" | "sync">;
export type SyncPhase = "disabled" | "processing" | "warnings" | "ready" | "failed";

/** Runs of a single document show its pipeline stage instead of a page count. */
const MIN_PAGES_FOR_COUNT = 2;

export interface PagesProgress {
  /** Pages that reached a terminal state (done or failed). */
  settled: number;
  total: number;
}

/** "37 of 43 pages" while a multi-page run is still running; `null` otherwise. */
export function pagesProgress(source: SyncSource): PagesProgress | null {
  const sync = source.sync;
  if (sync?.state !== "running" || sync.pages_total < MIN_PAGES_FOR_COUNT) return null;
  return { settled: Math.min(sync.pages_total, sync.pages_done + sync.pages_failed), total: sync.pages_total };
}

/** Pages of a finished run that could not be processed (the source is still usable). */
export function failedPages(source: SyncSource): number {
  return source.status === "ready" && source.sync?.state === "partial" ? source.sync.pages_failed : 0;
}

export function syncPhase(source: SyncSource): SyncPhase {
  if (source.status === "disabled") return "disabled";
  if (source.status === "failed") return "failed";
  if (source.status === "ready") return failedPages(source) > 0 ? "warnings" : "ready";
  return "processing";
}
